import { useEffect, useState } from "react";
import { Plus, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

export function NewBudgetDialog({ open, onOpenChange, clubId, historicals, budgets, suggestedYear, onCreated }) {
  const t = useT();
  const [year, setYear] = useState(suggestedYear);
  const [source, setSource] = useState("historical");
  const [sourceYear, setSourceYear] = useState(null);
  const [inflation, setInflation] = useState(0.03);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setYear(suggestedYear);
      setSource("historical");
      const latestHist = (historicals || []).length ? Math.max(...historicals.map((h) => h.year)) : null;
      setSourceYear(latestHist);
      setInflation(0.03);
    }
  }, [open, suggestedYear, historicals]);

  // When the user switches between "historical" and "budget", reset sourceYear
  // to the latest entry of the newly-active list. Without this, the <select>
  // renders the first option of the new list while state still points at a
  // value from the previous list → 404 on prefill.
  useEffect(() => {
    if (!open) return;
    if (source === "historical") {
      const latest = (historicals || []).length ? Math.max(...historicals.map((h) => h.year)) : null;
      setSourceYear(latest);
    } else if (source === "budget") {
      const latest = (budgets || []).length ? Math.max(...budgets.map((b) => b.year)) : null;
      setSourceYear(latest);
    }
  }, [source, open, historicals, budgets]);

  const submit = async () => {
    setSaving(true);
    try {
      let payload;
      if (source === "empty") {
        payload = { year: parseInt(year, 10) };
      } else {
        const { data: prefill } = await api.post(`/clubs/${clubId}/budgets/prefill`, {
          year: parseInt(year, 10),
          source,
          source_year: sourceYear ? parseInt(sourceYear, 10) : null,
          inflation: parseFloat(inflation || 0),
        });
        payload = { ...prefill, year: parseInt(year, 10) };
      }
      await api.post(`/clubs/${clubId}/budgets`, payload);
      toast.success(t("Begroting aangemaakt"));
      onCreated?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || String(e.message || e));
    } finally {
      setSaving(false);
    }
  };

  const histYears = (historicals || []).map((h) => h.year).sort((a, b) => b - a);
  const budgetYears = (budgets || []).map((b) => b.year).sort((a, b) => b - a);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl" data-testid="new-budget-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-black text-2xl uppercase tracking-tight flex items-center gap-2 pr-8">
            <Sparkles className="w-5 h-5" /> {t("Nieuwe begroting")}
          </DialogTitle>
          <DialogDescription>
            {t("Kies het jaar en een handig startpunt. Je kunt daarna de cijfers alsnog aanpassen.")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <div className="stat-label mb-1">{t("Jaar")}</div>
            <input
              type="number"
              value={year}
              onChange={(e) => setYear(e.target.value)}
              data-testid="new-budget-year"
              className="border-2 border-foreground bg-background px-3 py-2 font-mono-num w-32 focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div>
            <div className="stat-label mb-2">{t("Startpunt")}</div>
            <div className="space-y-2">
              <SourceOption
                testid="new-budget-source-empty"
                selected={source === "empty"}
                onClick={() => setSource("empty")}
                title={t("Leeg starten")}
                desc={t("Alle bedragen op nul. Je typt zelf alles in.")}
              />
              <SourceOption
                testid="new-budget-source-historical"
                selected={source === "historical"}
                onClick={() => setSource("historical")}
                title={t("Kopieer laatste jaarrekening × inflatie")}
                desc={t("Vult de begroting met het geselecteerde resultaatsjaar, vermenigvuldigd met 1 + inflatie.")}
                disabled={histYears.length === 0}
              />
              <SourceOption
                testid="new-budget-source-budget"
                selected={source === "budget"}
                onClick={() => setSource("budget")}
                title={t("Kopieer vorige begroting × inflatie")}
                desc={t("Handig als je gewoon een jaartje verder wilt met dezelfde koers.")}
                disabled={budgetYears.length === 0}
              />
            </div>
          </div>
          {source !== "empty" && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="stat-label mb-1">{t("Bron-jaar")}</div>
                <select
                  value={sourceYear || ""}
                  onChange={(e) => setSourceYear(e.target.value)}
                  data-testid="new-budget-source-year"
                  className="border-2 border-foreground bg-background px-3 py-2 font-mono-num w-full"
                >
                  {(source === "historical" ? histYears : budgetYears).map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
              <div>
                <div className="stat-label mb-1">{t("Inflatie (fractie)")}</div>
                <input
                  type="number" step="0.001" min="0" max="0.5"
                  value={inflation}
                  onChange={(e) => setInflation(e.target.value)}
                  data-testid="new-budget-inflation"
                  className="border-2 border-foreground bg-background px-3 py-2 font-mono-num w-full"
                />
              </div>
            </div>
          )}
        </div>
        <DialogFooter className="mt-4">
          <button onClick={() => onOpenChange(false)} disabled={saving}
            data-testid="new-budget-cancel"
            className="btn-brutal bg-background text-foreground px-4 py-2 font-display uppercase font-bold">
            {t("Annuleren")}
          </button>
          <button onClick={submit} disabled={saving}
            data-testid="new-budget-submit"
            className="btn-brutal bg-primary text-primary-foreground px-4 py-2 font-display uppercase font-bold inline-flex items-center gap-2">
            <Plus className="w-4 h-4" /> {saving ? t("Bezig…") : t("Aanmaken")}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function SourceOption({ testid, selected, onClick, title, desc, disabled = false }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`w-full text-left border-2 p-3 transition ${selected ? "border-foreground bg-primary/10" : "border-foreground/30 hover:border-foreground"} disabled:opacity-40 disabled:cursor-not-allowed`}
    >
      <div className="font-display font-black uppercase tracking-tight text-sm">{title}</div>
      <div className="text-xs text-muted-foreground mt-1">{desc}</div>
    </button>
  );
}
